import type {PickedVideo} from "../sponsorOptions";

/**
 * What a browser can tell us about a picked video before a byte is uploaded
 * (kitecorp/transferify-ui#1868).
 *
 * The rule that judges the answer lives in `sponsorOptions.ts` and is tested there against literals.
 * This module is only the INSTRUMENT: it decodes just enough of the file to learn its duration, which
 * is a browser API and therefore untestable as data. Keeping the two apart is what lets the duration
 * bound be asserted without a real video and without a DOM.
 *
 * Nothing here runs at module scope, so a pre-rendered page never touches `document` (ui#74).
 */

/**
 * How long to wait for a media element to say anything at all.
 *
 * Only the container header is being read (`preload="metadata"`), so a browser that is going to answer
 * has answered long before this; the value is a ceiling on silence rather than a budget for work. Eight
 * seconds is generous for a file already on the reader's own disk and short enough that an advertiser
 * does not read the picker as broken.
 */
const METADATA_TIMEOUT_MS = 8_000;

/**
 * Reads a picked video's duration, its frame size, its declared type and its byte size.
 *
 * `preload="metadata"` is what makes this cheap: the element fetches the container header rather than
 * the stream, so an 8 MB file costs a few kilobytes of decode. A file the browser cannot open resolves
 * with `readable: false` rather than rejecting: it is an ANSWER, and the rule's answer to it is to send
 * the file and let the api judge — a rejection here would have to be translated into that anyway.
 *
 * The object URL is revoked on every path, including the error one: a leaked blob URL pins the whole
 * file in memory for the life of the document, and this picker is one an advertiser retries.
 */
export function readVideoMetadata(
	file: File, timeoutMs: number = METADATA_TIMEOUT_MS,
): Promise<PickedVideo> {
	return new Promise<PickedVideo>(resolve => {
		const element = document.createElement("video");
		const source = URL.createObjectURL(file);
		let settled = false;
		const settle = (readable: boolean, seconds: number) => {
			if (settled) {
				return;
			}
			settled = true;
			window.clearTimeout(giveUp);
			URL.revokeObjectURL(source);
			/*
			 * The FRAME SIZE, read BEFORE the source is detached (kitecorp/transferify-ui#2143).
			 *
			 * Order is the correctness here: removing `src` runs the media load algorithm again, which
			 * resets the element's intrinsic dimensions to zero - so reading after it would report an
			 * unmeasured clip for a file the browser had just measured. Zero on both axes is a real
			 * answer on the paths where nothing opened, and `isTooNarrowForWallpaper` fails open on it.
			 */
			const {videoWidth, videoHeight} = element;
			element.removeAttribute("src");
			resolve({
				type: file.type,
				size: file.size,
				readable,
				seconds,
				width: videoWidth,
				height: videoHeight,
			});
		};
		/*
		 * THE PROMISE MUST SETTLE, and the two events are not enough to guarantee it.
		 *
		 * A media element that fires neither `loadedmetadata` nor `error` is not hypothetical — jsdom is
		 * one, which is why `useSponsorBooking.test.tsx` mocks this module rather than calling it — and
		 * the cost of one that hangs is not a slow picker. The caller awaits this inside
		 * `flight.run("pick-video")`, and `KeyedSingleFlight` releases the key only when the tracked
		 * promise settles; a promise that never does holds that key for the life of the page, hands
		 * itself to every later pick, and never reaches the `setUploading(true)` that would have
		 * disabled the control. The advertiser gets no spinner, no message, and no retry that can work.
		 *
		 * Giving up answers `readable: false`, which is the same answer a browser with no decoder gives,
		 * and the rule already knows what to do with it: send the file and let the api judge. So the
		 * failure mode of this timer firing early is one upload, not one refusal.
		 */
		const giveUp = window.setTimeout(() => settle(false, Number.NaN), timeoutMs);
		element.preload = "metadata";
		// Muted and never appended to the document: nothing about this plays, and an element outside the
		// tree cannot be autoplay-blocked or heard.
		element.muted = true;
		element.onloadedmetadata = () => settle(true, element.duration);
		// `error` means this browser could not OPEN the file, which is not a verdict about the file —
		// see {@link PickedVideo.readable}. Reported as such rather than as an unreadable duration, so
		// the rule can fail open instead of refusing an upload the api would have accepted.
		element.onerror = () => settle(false, Number.NaN);
		element.src = source;
	});
}
